/**
 * Pressing Place, and what a failed attempt leaves behind (PT-06, V5, docs/15).
 *
 * The order id is the server's idempotency key: an id sent again answers as
 * its first attempt did. That makes a retry safe only while it is the same
 * order. A connection can drop after the server has placed the order and
 * before its answer arrives, so a failed Place may have gone through - and a
 * customer who then changes the basket is sending a different order. Sent
 * under the old id, the change would come back "placed" as the earlier order
 * and be lost without a word; sent under a new id without looking, it could be
 * a second order beside the first.
 *
 * So a failed attempt is remembered, request and all. Sending the same order
 * again keeps the id. A changed order first reads My orders: if the old id is
 * there, the earlier attempt went through, and the customer is told so and
 * shown it - the change is not sent. If it is not there, nothing went, and the
 * changed order goes under a new id. The server refuses a different order
 * under a used id as well, and that refusal is read the same way.
 *
 * One gap stays open: an attempt the server is still working through when My
 * orders is read looks like one that never arrived. Closing it needs the
 * server to say so, not the page.
 */

import type { PlaceRequest, PortalApi, PortalError, WebOrder } from './portal-api.js';

export interface Attempt {
  /** The id the next Place sends. */
  readonly id: string;
  /**
   * What the last failed Place sent under `id`, which may have gone through.
   * Absent while nothing has been sent under it.
   */
  readonly tried?: PlaceRequest;
}

/** How pressing Place ended, when it ended the basket. */
export type Settled =
  /** This order was placed. */
  | { readonly kind: 'placed'; readonly reference: string }
  /**
   * An earlier attempt had already placed the order, and this one was not
   * sent, or was refused. The reference is absent only when My orders could
   * not be read to find it.
   */
  | { readonly kind: 'earlier'; readonly reference?: string };

export type Placing =
  | Settled
  /** Not placed, or not known to be: the basket stays, and `next` is the next try. */
  | { readonly kind: 'failed'; readonly error: PortalError; readonly next: Attempt }
  /**
   * The order changed after a failed attempt and My orders could not be read
   * to see whether that attempt went. Nothing was sent; the attempt is as it was.
   */
  | { readonly kind: 'unchecked' };

type Line = PlaceRequest['lines'][number];

function sameLine(a: Line, b: Line): boolean {
  return (
    a.product_id === b.product_id &&
    a.quantity === b.quantity &&
    a.shown_unit_price_cents === b.shown_unit_price_cents
  );
}

/**
 * Whether two requests ask for the same order: the same lines in the same
 * order, the same address, date and note. The id is left out - it is what is
 * being decided.
 */
export function sameOrder(a: PlaceRequest, b: PlaceRequest): boolean {
  return (
    a.lines.length === b.lines.length &&
    a.lines.every((line, index) => {
      const other = b.lines[index];
      return other !== undefined && sameLine(line, other);
    }) &&
    a.delivery_address_id === b.delivery_address_id &&
    a.required_date === b.required_date &&
    a.note === b.note
  );
}

/** The order an id placed, none, or `unread` when My orders could not be read. */
async function placedUnder(
  api: Pick<PortalApi, 'orders'>,
  id: string,
): Promise<WebOrder | undefined | 'unread'> {
  const answer = await api.orders();
  if (!answer.ok) return 'unread';
  return answer.value.find((order) => order.id === id);
}

/**
 * Places the order `build` makes for an id, against what the last attempt
 * left. `mint` makes a new id, used only once the old one is known to hold no
 * order.
 */
export async function placeOrder(
  api: Pick<PortalApi, 'place' | 'orders'>,
  attempt: Attempt,
  build: (id: string) => PlaceRequest,
  mint: () => string,
): Promise<Placing> {
  let request = build(attempt.id);

  if (attempt.tried !== undefined && !sameOrder(attempt.tried, request)) {
    const earlier = await placedUnder(api, attempt.id);
    if (earlier === 'unread') return { kind: 'unchecked' };
    if (earlier !== undefined) return { kind: 'earlier', reference: earlier.reference };
    request = build(mint());
  }

  const answer = await api.place(request);
  if (answer.ok) return { kind: 'placed', reference: answer.value.reference };

  if (answer.error.kind === 'id-used') {
    // The server has the order under this id, whatever My orders can tell us.
    const earlier = await placedUnder(api, request.id);
    return earlier === undefined || earlier === 'unread'
      ? { kind: 'earlier' }
      : { kind: 'earlier', reference: earlier.reference };
  }

  return {
    kind: 'failed',
    error: answer.error,
    next: { id: request.id, tried: request },
  };
}
